/**
 * Ask compliance about one CMS subsection. A button opens a small composer;
 * the question goes to the compliance team (bell and email) and the answer
 * comes back here and by email. Used under My items (per subsection) and on
 * the item page (the item's own subsection).
 */
import React, { useState } from 'react';
import { api, fmtWhen } from './api.js';
import { Button, inputCls, Pill } from './ui.jsx';

/**
 * questionKind 'reassign' turns the composer into a request to move the item
 * to someone else. The owner cannot change ownership; compliance does, from
 * the item page, after reading the request.
 */
export function AskComplianceButton({ topicKey, label, itemId = null, onAsked, kind = 'secondary', className = '', questionKind = 'question', buttonLabel = null, preset = '' }) {
  const reassign = questionKind === 'reassign';
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(preset);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setErr(null);
    const d = await api('/questions', { method: 'POST', body: { topic_key: topicKey, item_id: itemId, body: text.trim(), kind: questionKind } });
    setBusy(false);
    if (!d?.success) { setErr(d?.message || 'Could not send the question'); return; }
    setText(preset); setDone(true); onAsked?.(d.question);
    setTimeout(() => { setDone(false); setOpen(false); }, 1800);
  };
  return (
    <span className={`relative inline-block ${className}`}>
      <Button kind={kind} onClick={() => setOpen((o) => !o)} className="!px-3 !py-1.5 !text-xs" title={reassign ? 'Only compliance can change who an item is assigned to. Ask, and they decide.' : undefined}>{open ? 'Close' : (buttonLabel || (reassign ? 'Ask compliance to reassign' : 'Ask compliance'))}</Button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-[26rem] max-w-[90vw] rounded-2xl border border-orange-200 bg-white shadow-xl p-4 text-left" onClick={(e) => e.stopPropagation()}>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{reassign ? 'Ask compliance to reassign' : 'Ask compliance about'}</div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5">{label}</div>
          {reassign && <div className="text-xs text-slate-600 mt-1">You cannot change who an item is assigned to. Say who should have it and why; compliance makes the change and you are told.</div>}
          <textarea rows={4} autoFocus className={`${inputCls} mt-3`} value={text} onChange={(e) => setText(e.target.value)} placeholder={reassign ? 'Who should have this item, and why it is not yours.' : 'What is unclear, what you have, what you need from compliance.'} onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(); }} />
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-500">{done ? <span className="text-emerald-700 font-semibold">Sent. Compliance has been told; the answer comes back here and by email.</span> : 'The compliance team gets a bell and an email. Ctrl+Enter sends.'}</span>
            <Button onClick={send} disabled={busy || !text.trim()}>{busy ? 'Sending…' : 'Send'}</Button>
          </div>
          {err && <div className="text-xs text-rose-600 mt-1">{err}</div>}
        </div>
      )}
    </span>
  );
}

/** A person's questions on one subsection, newest first, with compliance's answer when it came. */
export function QuestionThread({ questions = [] }) {
  if (!questions.length) return null;
  return (
    <div className="space-y-2 mt-2">
      {questions.map((q) => (
        <div key={q.id} className={`rounded-xl border px-3 py-2 text-sm ${q.status === 'answered' ? 'border-emerald-200 bg-emerald-50/40' : 'border-orange-200 bg-orange-50/40'}`}>
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
            <Pill tone={q.status === 'answered' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-orange-50 text-orange-800 ring-orange-200'}>{q.status === 'answered' ? 'Answered' : 'Waiting on compliance'}</Pill>
            {q.kind === 'reassign' && <Pill tone="bg-violet-50 text-violet-800 ring-violet-200">Reassignment request</Pill>}
            <span>asked {fmtWhen(q.created_at)}{q.item_cms_ref ? ` · about ${q.item_cms_ref}` : ''}</span>
          </div>
          <div className="text-slate-800 whitespace-pre-wrap mt-1">{q.body}</div>
          {q.status === 'answered' && (
            <div className="mt-2 border-l-2 border-emerald-400 pl-3">
              <div className="text-[11px] text-slate-500">{q.answered_by_name || 'Compliance'} · {fmtWhen(q.answered_at)}</div>
              <div className="text-slate-900 whitespace-pre-wrap">{q.answer}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
